import { useQuery, useMutation, useQueryClient, useInfiniteQuery } from "@tanstack/react-query";
import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { getTranslatedName, getCurrentLang } from "@/lib/translation-utils";
import KanbanColumn from "@/components/crm/KanbanColumn";
import { useAuthLite } from "@/context/AuthLite";
import { apiUrl, apiFetch } from "@/lib/api-client";
import { logger } from "@/lib/logger";
import {
  buildUserScopedKey,
  loadSortPrefV1,
  migrateAnonToUserScopedV1,
  saveSortPrefV1,
} from "@/lib/user-scoped-storage";
import type { Deal, Contact, Stage, Pipeline, Board, BoardResponse, ContactsListResponse } from "@shared/api";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { DatePickerPopover } from "@/components/ui/date-picker-popover";
import { AppSheet, AppSheetFooterButton } from "@/components/ui/app-sheet";
import { useNavigate } from "react-router-dom";
import SortControls, { type SortBy, type SortOrder } from "@/components/crm/SortControls";
import { Button } from "@/components/ui/button";
import { MoveRight, Trash2, Download, MoreHorizontal, FileSpreadsheet, FileText, Loader2, Plus, X, BarChart3, CalendarClock, ClipboardCheck } from "lucide-react";
import * as XLSX from "xlsx";
import { PDFDocument, rgb, StandardFonts } from "pdf-lib";
import { cn } from "@/lib/utils";
import { dealIsClosingOverdue } from "@/lib/crm-closing-date";
import { getDealInstallationDateFromRow } from "@/lib/crm-deal-stage";
import { InstallationDateDialog } from "@/components/crm/InstallationDateDialog";
import { isInstallationDateCaptureStageRow } from "../../functions/lib/deal-installation-date";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { CRMBoardSkeleton } from "@/components/crm/CRMBoardSkeleton";
import { useToast } from "@/hooks/use-toast";
import { useIsMobile } from "@/hooks/use-mobile";
import FloatingActionButton from "@/components/FloatingActionButton";
import { FabMenu } from "@/components/ui/fab-menu";
import PageHeader from "@/components/PageHeader";
import Loading from "@/components/Loading";
import { PageTitleBar } from "@/components/ui/PageTitleBar";
import { SelectionToolbarLayout } from "@/components/ui/selection-toolbar-layout";
import { ContactSelectCombobox } from "@/components/crm/ContactSelectCombobox";
import { preventDismissWhenPointerOnRadixPopper } from "@/lib/radix-outside-popover-guard";

const CRM_SORT_FEATURE_KEY = "crm.sort";
const CRM_SORT_BY_ALLOWED = ["title", "closingDate", "createdAt"] as const;
const CRM_SORT_ORDER_ALLOWED = ["asc", "desc"] as const;

const MOCK_BOARD = {
  pipelines: [{ id: "p-main", name: "Sales Pipeline", order: 0 }],
  stages: [
    { id: "s-new", pipelineId: "p-main", name: "Új megkeresés", order: 0 },
    { id: "s-qualify", pipelineId: "p-main", name: "Érdeklődés", order: 1 },
    { id: "s-proposal", pipelineId: "p-main", name: "Kell ajánlat", order: 2 },
    {
      id: "s-negotiation",
      pipelineId: "p-main",
      name: "Jóváhagyásra várunk",
      order: 3,
    },
    {
      id: "s-closed",
      pipelineId: "p-main",
      name: "Végső / egyeztetés",
      order: 4,
    },
  ],
  deals: [],
  contacts: [],
};

export default function CRMPage() {
  logger.debug("Component render start");
  const { t, i18n } = useTranslation();
  const lang = getCurrentLang(i18n.language);
  const { user } = useAuthLite();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { toast } = useToast();
  const isMobile = useIsMobile();
  
  // Debug: Log component render
  useEffect(() => {
    logger.debug("Component mounted");
    return () => {
      logger.debug("Component unmounting");
    };
  }, []);

  // UI state
  const [open, setOpen] = useState(false);
  const [newDealSheetHost, setNewDealSheetHost] = useState<HTMLElement | null>(null);
  const [title, setTitle] = useState("");
  const [stageId, setStageId] = useState<string>("");
  const [contactId, setContactId] = useState<string>("");
  const [newContactName, setNewContactName] = useState("");
  const [newContactEmail, setNewContactEmail] = useState("");
  const [newContactPhone, setNewContactPhone] = useState("");
  const [newContactCity, setNewContactCity] = useState("");
  const [newContactStreet, setNewContactStreet] = useState("");
  const [closingDate, setClosingDate] = useState<string>(() =>
    new Date().toISOString().slice(0, 10),
  );
  // Deal site address (separate from client address)
  const [dealSiteCity, setDealSiteCity] = useState<string>("");
  const [dealSiteAddress, setDealSiteAddress] = useState<string>("");
  // Form validation state
  const [touchedFields, setTouchedFields] = useState<Set<string>>(new Set());
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const [selectedSubPipelineId, setSelectedSubPipelineId] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState<SortBy>("createdAt");
  const [sortOrder, setSortOrder] = useState<SortOrder>("desc");
  const [showOverdueClosingOnly, setShowOverdueClosingOnly] = useState(false);
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const hasLoadedPreferencesRef = useRef(false);
  const hasHydratedLocalSortPrefRef = useRef(false);
  
  // Bulk operations state
  const [selectedDeals, setSelectedDeals] = useState<Set<string>>(new Set());
  const [selectionModeEnabled, setSelectionModeEnabled] = useState(false);
  const [showBulkMoveDialog, setShowBulkMoveDialog] = useState(false);
  const [bulkMovePipelineId, setBulkMovePipelineId] = useState<string>("");
  const [bulkMoveStageId, setBulkMoveStageId] = useState<string>("");
  const [showBulkOwnerDialog, setShowBulkOwnerDialog] = useState(false);
  const [selectedOwnerEmail, setSelectedOwnerEmail] = useState<string>("");
  const [showExportDialog, setShowExportDialog] = useState(false);
  const [installationPrompt, setInstallationPrompt] = useState<{
    dealId: string;
    stageId: string;
    dealTitle: string;
  } | null>(null);
  const [installationConfirming, setInstallationConfirming] = useState(false);
  
  // Export dialog state
  const [exportFormat, setExportFormat] = useState<"csv" | "excel" | "pdf">("csv");
  const [selectedColumns, setSelectedColumns] = useState<Set<string>>(new Set([
    "title", "contactName", "email", "phone", "address", "city", 
    "stage", "value", "currency", "createdAt", "closingDate"
  ]));
  
  // Available columns for export
  const availableColumns = [
    { id: "title", labelKey: "crm.colName" as const },
    { id: "contactName", labelKey: "crm.colContact" as const },
    { id: "email", labelKey: "crm.colEmail" as const },
    { id: "phone", labelKey: "crm.colPhone" as const },
    { id: "address", labelKey: "crm.colAddress" as const },
    { id: "city", labelKey: "crm.colCity" as const },
    { id: "stage", labelKey: "crm.colStage" as const },
    { id: "value", labelKey: "crm.colValue" as const },
    { id: "currency", labelKey: "crm.colCurrency" as const },
    { id: "width", labelKey: "crm.colWidth" as const },
    { id: "height", labelKey: "crm.colHeight" as const },
    { id: "color", labelKey: "crm.colColor" as const },
    { id: "surface", labelKey: "crm.colSurface" as const },
    { id: "emboss", labelKey: "crm.colEmboss" as const },
    { id: "createdAt", labelKey: "crm.colCreatedAt" as const },
    { id: "closingDate", labelKey: "crm.colClosingDate" as const },
  ];
  
  // Selection mode is active when enabled (allows selecting deals)
  const selectionMode = selectionModeEnabled;

  // Lazy loading: track loaded deals per stage
  // CRITICAL: These hooks MUST be called before any early returns
  // to comply with React Rules of Hooks
  const INITIAL_DEALS_PER_STAGE = 50;
  const DEALS_LOAD_INCREMENT = 50;
  const [dealsLimitPerStage, setDealsLimitPerStage] = useState<Record<string, number>>({});
  const [loadingStages, setLoadingStages] = useState<Set<string>>(new Set());
  const [totalDealsByStage, setTotalDealsByStage] = useState<Record<string, number>>({});
  const [loadedDealsByStage, setLoadedDealsByStage] = useState<Record<string, Deal[]>>({});
  
  // Refs for tracking previous values in useEffect
  // CRITICAL: These hooks MUST be called before any early returns
  // to comply with React Rules of Hooks
  const prevSubPipelineRef = useRef<string | null>(null);
  const prevBoardDataRef = useRef<{ dealsCount: number; stagesCount: number; dealsHash: string } | null>(null);
  /** Incremented on each move onSuccess; background stage reload only applies result if generation still matches (prevents stale overwrite). */
  const moveRefreshGenerationRef = useRef(0);

  // Data
  const board = useQuery({
    queryKey: ["crm-board"],
    queryFn: async () => {
      const r = await apiFetch("/api/crm/board", {
        credentials: "include",
      });
      if (!r.ok) throw new Error("Pipelines betöltése sikertelen");
      const data = await r.json() as BoardResponse;
      const out = {
        pipelines: data.pipelines,
        stages: data.stages,
        deals: data.deals,
        contacts: data.contacts,
      };
      return out;
    },
    staleTime: 0,
    refetchOnWindowFocus: true,
    refetchInterval: 25_000,
    refetchIntervalInBackground: false,
  });

  const contacts = useQuery({
    queryKey: ["crm-contacts"],
    queryFn: async () => {
      const r = await apiFetch("/api/crm/contacts", {
        credentials: "include",
      });
      if (!r.ok) throw new Error("Kontaktek betöltése sikertelen");
      const data = await r.json();
      return (data.contacts || []) as Contact[];
    },
  });

  // Removed useStageDealsQuery - now using board.data.deals with client-side filtering
  // This avoids the hook-in-loop problem and React error #310

  // Keyboard shortcuts: N to create new deal (only on CRM page)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // N key: Create new deal (only if not typing in an input)
      if (e.key === 'n' || e.key === 'N') {
        const target = e.target as HTMLElement;
        const isInput = target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable;
        if (!isInput && !e.ctrlKey && !e.metaKey) {
          e.preventDefault();
          setOpen(true);
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  // ESC key handler to exit selection mode
  useEffect(() => {
    if (!selectionModeEnabled) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setSelectionModeEnabled(false);
        setSelectedDeals(new Set());
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectionModeEnabled]);

  // Load user preferences on mount
  // IMPORTANT: Preferences are per-user (by email), so each account
  // has its own collapsed column state
  useEffect(() => {
      if (!user?.email) {
      logger.debug("Waiting for user to load before loading preferences");
      return;
    }

    const loadPreferences = async () => {
      try {
        // Local cache/fallback for sort prefs (fast UI, survives API failure)
        if (!hasHydratedLocalSortPrefRef.current) {
          migrateAnonToUserScopedV1({ userEmail: user.email, featureKey: CRM_SORT_FEATURE_KEY });
          const scopedKey = buildUserScopedKey(user.email, CRM_SORT_FEATURE_KEY);
          const localPref = loadSortPrefV1<SortBy, SortOrder>(
            scopedKey,
            CRM_SORT_BY_ALLOWED,
            CRM_SORT_ORDER_ALLOWED,
            { sortBy: "createdAt", sortOrder: "desc" },
          );
          setSortBy(localPref.sortBy);
          setSortOrder(localPref.sortOrder);
          if (localPref.sortBy !== "createdAt" || localPref.sortOrder !== "desc") {
            logger.debug("Local sort cache detected, clearing loadedDealsByStage to reload from backend");
            setLoadedDealsByStage({});
          }
          hasHydratedLocalSortPrefRef.current = true;
        }

        logger.debug("Loading preferences for user:", user.email);
        const r = await apiFetch("/api/user/preferences", {
          credentials: "include",
        });
        if (r.ok) {
          const data = await r.json();
          logger.debug("Loaded preferences:", data.preferences);
          // Load collapsed columns state for this specific user
          if (data.preferences?.crmCollapsedColumns) {
            logger.debug("Setting collapsed columns:", data.preferences.crmCollapsedColumns);
            setCollapsed(data.preferences.crmCollapsedColumns);
          } else {
            logger.debug("No collapsed columns found in preferences, starting with empty state");
            setCollapsed({});
          }
          
          // Load sorting preferences
          const loadedSortBy = data.preferences?.crmSortBy || "createdAt";
          const loadedSortOrder = data.preferences?.crmSortOrder || "desc";
          
          if (data.preferences?.crmSortBy) {
            setSortBy(loadedSortBy);
          }
          if (data.preferences?.crmSortOrder) {
            setSortOrder(loadedSortOrder);
          }

          // Persist server preference to local cache as well
          saveSortPrefV1<SortBy, SortOrder>(
            buildUserScopedKey(user.email, CRM_SORT_FEATURE_KEY),
            { sortBy: loadedSortBy, sortOrder: loadedSortOrder },
          );
          
          // If user has custom sorting (not default), clear loadedDealsByStage
          // so that the sorting useEffect will load sorted deals from backend
          // This ensures correct sorting even on page refresh
          if (loadedSortBy !== "createdAt" || loadedSortOrder !== "desc") {
            logger.debug("Custom sorting detected, clearing loadedDealsByStage to reload from backend");
            setLoadedDealsByStage({});
          }
          
          hasLoadedPreferencesRef.current = true;
        } else {
          logger.error("Failed to load preferences, status:", r.status);
          hasLoadedPreferencesRef.current = true; // Mark as loaded even on error to allow saving
        }
      } catch (error) {
        logger.error("Failed to load preferences:", error);
      }
    };

    loadPreferences();
  }, [user?.email]);

  // Save preferences when they change (collapsed columns + sorting)
  const updatePreferences = useMutation({
    mutationFn: async (preferences: any) => {
      const r = await apiFetch("/api/user/preferences", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ preferences }),
      });
      if (!r.ok) throw new Error("Preferences mentése sikertelen");
      return r.json();
    },
  });

  // Save collapsed state when it changes
  // Preferences are saved per-user (by email), so each account
  // maintains its own collapsed column preferences
  useEffect(() => {
    if (!user?.email) {
      logger.debug("Cannot save preferences: no user email");
      return;
    }

    // Don't save until preferences have been loaded (to avoid overwriting with empty state)
    if (!hasLoadedPreferencesRef.current) {
      logger.debug("Skipping save: preferences not loaded yet");
      return;
    }

    // Always keep a local cache copy (user-scoped) for fast restore / offline-ish behavior
    saveSortPrefV1<SortBy, SortOrder>(
      buildUserScopedKey(user.email, CRM_SORT_FEATURE_KEY),
      { sortBy, sortOrder },
    );
    
    // Clear existing timeout
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    // Debounce save by 500ms
    saveTimeoutRef.current = setTimeout(async () => {
      try {
        logger.debug("Saving preferences for user:", user.email, "collapsed:", collapsed);
        const r = await apiFetch("/api/user/preferences", {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            preferences: {
              crmCollapsedColumns: collapsed,
              crmSortBy: sortBy,
              crmSortOrder: sortOrder,
            },
          }),
        });
        if (r.ok) {
          const data = await r.json();
          logger.debug("Preferences saved successfully:", data.preferences);
        } else {
          const errorText = await r.text();
          logger.error("Failed to save preferences, status:", r.status, errorText);
        }
      } catch (error) {
        logger.error("Failed to save preferences:", error);
      }
    }, 500);

    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [collapsed, sortBy, sortOrder, user?.email]);

  // Background stage refresh after move: runs two GETs in parallel, applies result only if generation still matches (no stale overwrite).
  const reloadAffectedStagesInBackground = useCallback(
    (
      stageIds: string[],
      effectiveSubPipelineId: string | null,
      sortByParam: SortBy,
      sortOrderParam: SortOrder,
      thisGeneration: number,
    ) => {
      if (stageIds.length === 0) return;
      const pipelineId = effectiveSubPipelineId || undefined;
      Promise.all(
        stageIds.map((stageIdToReload) => {
          const params = new URLSearchParams();
          params.set("stageId", stageIdToReload);
          params.set("limit", String(INITIAL_DEALS_PER_STAGE));
          params.set("offset", "0");
          params.set("sortBy", sortByParam);
          params.set("sortOrder", sortOrderParam);
          if (pipelineId) params.set("pipelineId", pipelineId);
          return fetch(apiUrl(`/api/crm/deals?${params.toString()}`), { credentials: "include" })
            .then((r) => (r.ok ? r.json() : null))
            .catch((err) => {
              logger.error("Error reloading deals after move:", err);
              return null;
            })
            .then((data) => (data ? { stageId: stageIdToReload, deals: data.deals ?? [], total: data.total ?? 0 } : null));
        }),
      ).then((results) => {
        // Only apply if no newer move started (stale response guard)
        if (moveRefreshGenerationRef.current !== thisGeneration) return;
        setLoadedDealsByStage((prev) => {
          const next = { ...prev };
          results.forEach((r, i) => {
            if (r) next[r.stageId] = r.deals;
          });
          return next;
        });
        setDealsLimitPerStage((prev) => {
          const next = { ...prev };
          results.forEach((r) => {
            if (r) next[r.stageId] = r.deals.length;
          });
          return next;
        });
        setTotalDealsByStage((prev) => {
          const next = { ...prev };
          results.forEach((r) => {
            if (r) next[r.stageId] = r.total;
          });
          return next;
        });
        qc.invalidateQueries({ queryKey: ["crm-board"] });
        qc.invalidateQueries({ queryKey: ["crm-deals"] });
      });
    },
    [qc],
  );

  const move = useMutation({
    mutationFn: async ({
      id,
      stageId,
      installationDate,
    }: {
      id: string;
      stageId: string;
      installationDate?: string;
    }) => {
      try {
        const url = apiUrl(`/api/crm/deals/${id}/stage`);
        logger.debug('Moving deal to:', url);
        const r = await fetch(url, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            stageId,
            ...(installationDate ? { installationDate } : {}),
          }),
        });
        if (!r.ok) {
          const body = await r.json().catch(() => ({}));
          if ((body as { error?: string }).error === "missing_installation_date") {
            throw new Error("missing_installation_date");
          }
          throw new Error(`Mozgatás sikertelen: ${r.status}`);
        }
        return r.json();
      } catch (err) {
        logger.error('Deal move error:', err);
        throw new Error(`Move hiba: ${err instanceof Error ? err.message : 'Fetch failed'}`);
      }
    },
    onMutate: async ({ id, stageId }) => {
      // Optimistic update: immediately update UI without waiting for server response
      // Find the deal in the current state
      let dealToMove: any = null;
      let oldStageId: string | null = null;
      
      // Search for the deal in loadedDealsByStage
      for (const [currentStageId, deals] of Object.entries(loadedDealsByStage)) {
        const foundDeal = deals.find((d: any) => d.id === id);
        if (foundDeal) {
          dealToMove = foundDeal;
          oldStageId = currentStageId;
          break;
        }
      }
      
      // If not found in loaded deals, search in board.data.deals
      if (!dealToMove && board.data?.deals) {
        dealToMove = board.data.deals.find((d: any) => d.id === id);
        if (dealToMove) {
          oldStageId = dealToMove.stageId || dealToMove.stage_id || null;
        }
      }
      
      if (!dealToMove || !oldStageId || oldStageId === stageId) {
        // Deal not found or already in target stage, skip optimistic update
        return { dealToMove: null, oldStageId: null };
      }
      
      // Update the deal's stageId
      const updatedDeal = { ...dealToMove, stageId, stage_id: stageId };
      
      // Optimistically update loadedDealsByStage
      setLoadedDealsByStage((prev) => {
        const next = { ...prev };
        
        // Remove from old stage
        if (next[oldStageId]) {
          next[oldStageId] = next[oldStageId].filter((d: any) => d.id !== id);
        }
        
        // Add to new stage
        if (!next[stageId]) {
          next[stageId] = [];
        }
        next[stageId] = [...next[stageId], updatedDeal];
        
        return next;
      });
      
      // Update total counts
      setTotalDealsByStage((prev) => {
        const next = { ...prev };
        if (next[oldStageId] !== undefined) {
          next[oldStageId] = Math.max(0, (next[oldStageId] || 0) - 1);
        }
        next[stageId] = (next[stageId] || 0) + 1;
        return next;
      });
      
      // Return context for potential rollback
      return { dealToMove, oldStageId };
    },
    onError: (err, variables, context) => {
      // Rollback optimistic update on error
      if (context?.dealToMove && context?.oldStageId) {
        const { dealToMove, oldStageId } = context;
        const { id, stageId: newStageId } = variables;
        
        setLoadedDealsByStage((prev) => {
          const next = { ...prev };
          
          // Remove from new stage (where we optimistically added it)
          if (next[newStageId]) {
            next[newStageId] = next[newStageId].filter((d: any) => d.id !== id);
          }
          
          // Add back to old stage
          if (!next[oldStageId]) {
            next[oldStageId] = [];
          }
          next[oldStageId] = [...next[oldStageId], dealToMove];
          
          return next;
        });
        
        // Rollback total counts
        setTotalDealsByStage((prev) => {
          const next = { ...prev };
          if (next[newStageId] !== undefined) {
            next[newStageId] = Math.max(0, (next[newStageId] || 0) - 1);
          }
          next[oldStageId] = (next[oldStageId] || 0) + 1;
          return next;
        });
      }
      
      toast({
        title: t("common.error"),
        description: err instanceof Error ? err.message : t("crm.errorMove"),
        variant: "destructive",
      });
    },
    onSuccess: (data, variables, context) => {
      // Mutation finishes immediately; stage lists refresh in background. Only apply background result if generation still matches (no stale overwrite).
      const { stageId } = variables;
      const oldStageId = context?.oldStageId || null;
      const stagesToReload = [stageId];
      if (oldStageId && oldStageId !== stageId) {
        stagesToReload.push(oldStageId);
      }
      const subPipelines = board.data?.pipelines?.filter((p: any) => p.parent_pipeline_id) || [];
      const defaultSubPipelineId = subPipelines.length > 0 ? subPipelines[0].id : null;
      const effectiveSubPipelineId = selectedSubPipelineId || defaultSubPipelineId;
      const thisGeneration = ++moveRefreshGenerationRef.current;
      void reloadAffectedStagesInBackground(stagesToReload, effectiveSubPipelineId, sortBy, sortOrder, thisGeneration);
    },
  });

  const findDealById = useCallback(
    (id: string) => {
      for (const deals of Object.values(loadedDealsByStage)) {
        const found = deals.find((d: any) => d.id === id);
        if (found) return found;
      }
      return board.data?.deals?.find((d: any) => d.id === id) ?? null;
    },
    [loadedDealsByStage, board.data?.deals],
  );

  const handleDropDeal = useCallback(
    (id: string, stageId: string) => {
      const targetStage = (board.data?.stages ?? []).find((s: any) => s.id === stageId);
      const dealRow = findDealById(id);
      if (
        targetStage &&
        isInstallationDateCaptureStageRow(targetStage as unknown as Record<string, unknown>) &&
        !getDealInstallationDateFromRow(dealRow as unknown as Record<string, unknown>)
      ) {
        setInstallationPrompt({
          dealId: id,
          stageId,
          dealTitle: String((dealRow as any)?.title ?? (dealRow as any)?.name ?? id),
        });
        return;
      }
      move.mutate({ id, stageId });
    },
    [board.data?.stages, findDealById, move],
  );

  // Bulk operations
  const bulkMove = useMutation({
    mutationFn: async ({ dealIds, stageId, pipelineId }: { dealIds: string[]; stageId: string; pipelineId?: string }) => {
      const r = await fetch(apiUrl("/api/crm/deals/batch"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ operation: "move", dealIds, stageId, pipelineId }),
      });
      if (!r.ok) {
        const error = await r.json();
        throw new Error(error.message || "Tömeges mozgatás sikertelen");
      }
      return r.json();
    },
    onMutate: async ({ dealIds, stageId }) => {
      // Optimistic update: immediately update UI without waiting for server response
      const dealsToMove: Array<{ deal: any; oldStageId: string }> = [];
      
      // Find all deals to move
      for (const dealId of dealIds) {
        // Search in loadedDealsByStage
        for (const [currentStageId, deals] of Object.entries(loadedDealsByStage)) {
          const foundDeal = deals.find((d: any) => d.id === dealId);
          if (foundDeal) {
            dealsToMove.push({ deal: foundDeal, oldStageId: currentStageId });
            break;
          }
        }
        
        // If not found, search in board.data.deals
        if (!dealsToMove.find(d => d.deal.id === dealId) && board.data?.deals) {
          const foundDeal = board.data.deals.find((d: any) => d.id === dealId);
          if (foundDeal) {
            const oldStageId = foundDeal.stageId || foundDeal.stage_id || null;
            if (oldStageId) {
              dealsToMove.push({ deal: foundDeal, oldStageId });
            }
          }
        }
      }
      
      if (dealsToMove.length === 0) {
        return { dealsToMove: [] };
      }
      
      // Group deals by old stage for efficient updates
      const dealsByOldStage = new Map<string, any[]>();
      dealsToMove.forEach(({ deal, oldStageId }) => {
        if (!dealsByOldStage.has(oldStageId)) {
          dealsByOldStage.set(oldStageId, []);
        }
        dealsByOldStage.get(oldStageId)!.push(deal);
      });
      
      // Optimistically update loadedDealsByStage
      setLoadedDealsByStage((prev) => {
        const next = { ...prev };
        
        // Remove from old stages
        dealsByOldStage.forEach((deals, oldStageId) => {
          if (next[oldStageId]) {
            const dealIdsToRemove = new Set(deals.map((d: any) => d.id));
            next[oldStageId] = next[oldStageId].filter((d: any) => !dealIdsToRemove.has(d.id));
          }
        });
        
        // Add to new stage
        if (!next[stageId]) {
          next[stageId] = [];
        }
        const updatedDeals = dealsToMove.map(({ deal }) => ({
          ...deal,
          stageId,
          stage_id: stageId,
        }));
        next[stageId] = [...next[stageId], ...updatedDeals];
        
        return next;
      });
      
      // Update total counts
      setTotalDealsByStage((prev) => {
        const next = { ...prev };
        dealsByOldStage.forEach((deals, oldStageId) => {
          if (next[oldStageId] !== undefined) {
            next[oldStageId] = Math.max(0, (next[oldStageId] || 0) - deals.length);
          }
        });
        next[stageId] = (next[stageId] || 0) + dealsToMove.length;
        return next;
      });
      
      return { dealsToMove };
    },
    onError: (err, variables, context) => {
      // Rollback optimistic update on error
      if (context?.dealsToMove && context.dealsToMove.length > 0) {
        const { dealsToMove } = context;
        const { stageId: newStageId } = variables;
        
        // Group deals by old stage for efficient rollback
        const dealsByOldStage = new Map<string, any[]>();
        dealsToMove.forEach(({ deal, oldStageId }) => {
          if (!dealsByOldStage.has(oldStageId)) {
            dealsByOldStage.set(oldStageId, []);
          }
          dealsByOldStage.get(oldStageId)!.push(deal);
        });
        
        setLoadedDealsByStage((prev) => {
          const next = { ...prev };
          
          // Remove from new stage
          if (next[newStageId]) {
            const dealIdsToRemove = new Set(dealsToMove.map(({ deal }) => deal.id));
            next[newStageId] = next[newStageId].filter((d: any) => !dealIdsToRemove.has(d.id));
          }
          
          // Add back to old stages
          dealsByOldStage.forEach((deals, oldStageId) => {
            if (!next[oldStageId]) {
              next[oldStageId] = [];
            }
            next[oldStageId] = [...next[oldStageId], ...deals];
          });
          
          return next;
        });
        
        // Rollback total counts
        setTotalDealsByStage((prev) => {
          const next = { ...prev };
          if (next[newStageId] !== undefined) {
            next[newStageId] = Math.max(0, (next[newStageId] || 0) - dealsToMove.length);
          }
          dealsByOldStage.forEach((deals, oldStageId) => {
            next[oldStageId] = (next[oldStageId] || 0) + deals.length;
          });
          return next;
        });
      }
    },
    onSuccess: () => {
      // Silently invalidate queries in background (data is already optimistically updated)
      qc.invalidateQueries({ queryKey: ["crm-board"] });
      qc.invalidateQueries({ queryKey: ["crm-deals"] });
      // Don't reset state - we already updated it optimistically
      setSelectedDeals(new Set());
      setShowBulkMoveDialog(false);
    },
  });

  const bulkDelete = useMutation({
    mutationFn: async (dealIds: string[]) => {
      const r = await fetch(apiUrl("/api/crm/deals/batch"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ operation: "delete", dealIds }),
      });
      if (!r.ok) {
        const error = await r.json();
        throw new Error(error.message || "Tömeges törlés sikertelen");
      }
      return r.json();
    },
    onMutate: async (dealIds) => {
      // Optimistic update: immediately remove deals from UI without waiting for server response
      const dealsToDelete: Array<{ deal: any; oldStageId: string }> = [];
      
      // Find all deals to delete
      for (const dealId of dealIds) {
        // Search in loadedDealsByStage
        for (const [currentStageId, deals] of Object.entries(loadedDealsByStage)) {
          const foundDeal = deals.find((d: any) => d.id === dealId);
          if (foundDeal) {
            dealsToDelete.push({ deal: foundDeal, oldStageId: currentStageId });
            break;
          }
        }
        
        // If not found, search in board.data.deals
        if (!dealsToDelete.find(d => d.deal.id === dealId) && board.data?.deals) {
          const foundDeal = board.data.deals.find((d: any) => d.id === dealId);
          if (foundDeal) {
            const oldStageId = foundDeal.stageId || foundDeal.stage_id || null;
            if (oldStageId) {
              dealsToDelete.push({ deal: foundDeal, oldStageId });
            }
          }
        }
      }
      
      if (dealsToDelete.length === 0) {
        return { dealsToDelete: [] };
      }
      
      // Group deals by old stage for efficient updates
      const dealsByOldStage = new Map<string, any[]>();
      dealsToDelete.forEach(({ deal, oldStageId }) => {
        if (!dealsByOldStage.has(oldStageId)) {
          dealsByOldStage.set(oldStageId, []);
        }
        dealsByOldStage.get(oldStageId)!.push(deal);
      });
      
      // Optimistically remove deals from loadedDealsByStage
      setLoadedDealsByStage((prev) => {
        const next = { ...prev };
        
        // Remove from old stages
        dealsByOldStage.forEach((deals, oldStageId) => {
          if (next[oldStageId]) {
            const dealIdsToRemove = new Set(deals.map((d: any) => d.id));
            next[oldStageId] = next[oldStageId].filter((d: any) => !dealIdsToRemove.has(d.id));
          }
        });
        
        return next;
      });
      
      // Update total counts
      setTotalDealsByStage((prev) => {
        const next = { ...prev };
        dealsByOldStage.forEach((deals, oldStageId) => {
          if (next[oldStageId] !== undefined) {
            next[oldStageId] = Math.max(0, (next[oldStageId] || 0) - deals.length);
          }
        });
        return next;
      });
      
      return { dealsToDelete };
    },
    onError: (err, variables, context) => {
      // Rollback optimistic update on error
      if (context?.dealsToDelete && context.dealsToDelete.length > 0) {
        const { dealsToDelete } = context;
        
        // Group deals by old stage for efficient rollback
        const dealsByOldStage = new Map<string, any[]>();
        dealsToDelete.forEach(({ deal, oldStageId }) => {
          if (!dealsByOldStage.has(oldStageId)) {
            dealsByOldStage.set(oldStageId, []);
          }
          dealsByOldStage.get(oldStageId)!.push(deal);
        });
        
        setLoadedDealsByStage((prev) => {
          const next = { ...prev };
          
          // Add back to old stages
          dealsByOldStage.forEach((deals, oldStageId) => {
            if (!next[oldStageId]) {
              next[oldStageId] = [];
            }
            next[oldStageId] = [...next[oldStageId], ...deals];
          });
          
          return next;
        });
        
        // Rollback total counts
        setTotalDealsByStage((prev) => {
          const next = { ...prev };
          dealsByOldStage.forEach((deals, oldStageId) => {
            next[oldStageId] = (next[oldStageId] || 0) + deals.length;
          });
          return next;
        });
      }
      
      toast({
        title: t("common.error"),
        description: err instanceof Error ? err.message : t("crm.errorBulkDelete"),
        variant: "destructive",
      });
    },
    onSuccess: () => {
      // Reset all lazy loading state to force fresh data load
      // Also reset refs so useEffect will detect the change when board data refreshes
      setLoadedDealsByStage({});
      setDealsLimitPerStage({});
      setTotalDealsByStage({});
      setLoadingStages(new Set());
      prevBoardDataRef.current = null; // Force useEffect to re-initialize on next board data update
      setSelectedDeals(new Set());
      // Invalidate queries to refetch fresh data (data is already optimistically updated)
      qc.invalidateQueries({ queryKey: ["crm-board"] });
      qc.invalidateQueries({ queryKey: ["crm-deals"] });
      
      toast({
        title: t("common.success"),
        description: t("crm.successDeletes"),
      });
    },
  });

  // Fetch users for owner selection
  const usersQuery = useQuery({
    queryKey: ["admin-users"],
    queryFn: async () => {
      const r = await fetch(apiUrl("/api/admin/users"), {
        credentials: "include",
      });
      if (!r.ok) {
        // If not admin, return empty array instead of throwing
        if (r.status === 403) {
          return [];
        }
        throw new Error("Felhasználók betöltése sikertelen");
      }
      const data = await r.json();
      return (data.users || []) as Array<{ id: string; email: string; name?: string; role?: string }>;
    },
    enabled: showBulkOwnerDialog, // Only fetch when dialog is open
  });

  // Bulk owner change mutation
  const bulkOwnerChange = useMutation({
    mutationFn: async ({ dealIds, ownerEmail }: { dealIds: string[]; ownerEmail: string }) => {
      const r = await fetch(apiUrl("/api/crm/deals/batch"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ operation: "owner", dealIds, ownerEmail }),
      });
      if (!r.ok) {
        const error = await r.json();
        throw new Error(error.message || "Tulajdonos módosítás sikertelen");
      }
      return r.json();
    },
    onMutate: async ({ dealIds, ownerEmail }) => {
      // Optimistic update: immediately update owner email on deals without waiting for server response
      const dealsToUpdate: Array<{ deal: any; oldStageId: string; oldOwnerEmail: string }> = [];
      
      // Find all deals to update
      for (const dealId of dealIds) {
        // Search in loadedDealsByStage
        for (const [currentStageId, deals] of Object.entries(loadedDealsByStage)) {
          const foundDeal = deals.find((d: any) => d.id === dealId);
          if (foundDeal) {
            dealsToUpdate.push({ 
              deal: foundDeal, 
              oldStageId: currentStageId,
              oldOwnerEmail: foundDeal.ownerEmail || "",
            });
            break;
          }
        }
        
        // If not found, search in board.data.deals
        if (!dealsToUpdate.find(d => d.deal.id === dealId) && board.data?.deals) {
          const foundDeal = board.data.deals.find((d: any) => d.id === dealId);
          if (foundDeal) {
            const oldStageId = foundDeal.stageId || foundDeal.stage_id || null;
            if (oldStageId) {
              dealsToUpdate.push({ 
                deal: foundDeal, 
                oldStageId,
                oldOwnerEmail: foundDeal.ownerEmail || "",
              });
            }
          }
        }
      }
      
      if (dealsToUpdate.length === 0) {
        return { dealsToUpdate: [] };
      }
      
      // Optimistically update deals in loadedDealsByStage
      setLoadedDealsByStage((prev) => {
        const next = { ...prev };
        
        // Update owner email for each deal
        dealsToUpdate.forEach(({ deal, oldStageId }) => {
          if (next[oldStageId]) {
            next[oldStageId] = next[oldStageId].map((d: any) => {
              if (d.id === deal.id) {
                return {
                  ...d,
                  ownerEmail: ownerEmail,
                  owner_email: ownerEmail,
                };
              }
              return d;
            });
          }
        });
        
        return next;
      });
      
      return { dealsToUpdate };
    },
    onError: (err, variables, context) => {
      // Rollback optimistic update on error
      if (context?.dealsToUpdate && context.dealsToUpdate.length > 0) {
        const { dealsToUpdate } = context;
        
        setLoadedDealsByStage((prev) => {
          const next = { ...prev };
          
          // Rollback owner email for each deal
          dealsToUpdate.forEach(({ deal, oldStageId, oldOwnerEmail }) => {
            if (next[oldStageId]) {
              next[oldStageId] = next[oldStageId].map((d: any) => {
                if (d.id === deal.id) {
                  return {
                    ...d,
                    ownerEmail: oldOwnerEmail,
                    owner_email: oldOwnerEmail,
                  };
                }
                return d;
              });
            }
          });
          
          return next;
        });
      }
      
      toast({
        title: t("common.error"),
        description: err instanceof Error ? err.message : t("crm.errorOwner"),
        variant: "destructive",
      });
    },
    onSuccess: () => {
      setSelectedDeals(new Set());
      setShowBulkOwnerDialog(false);
      setSelectedOwnerEmail("");
      // Invalidate queries to refetch fresh data (data is already optimistically updated)
      qc.invalidateQueries({ queryKey: ["crm-board"] });
      qc.invalidateQueries({ queryKey: ["crm-deals"] });
      
      toast({
        title: t("common.success"),
        description: t("crm.successOwner"),
      });
    },
  });

  const handleSelectDeal = (dealId: string, checked: boolean) => {
    setSelectedDeals((prev) => {
      const next = new Set(prev);
      if (checked) {
        next.add(dealId);
      } else {
        next.delete(dealId);
      }
      return next;
    });
  };

  const handleSelectStage = (stageId: string) => {
    const stageDeals = dealsByStage.get(stageId) || [];
    const allStageDealIds = new Set(stageDeals.map((d: any) => d.id));
    
    // Check if all deals in stage are already selected
    const allSelected = stageDeals.length > 0 && stageDeals.every((d: any) => selectedDeals.has(d.id));
    
    if (allSelected) {
      // If all selected, deselect all deals in this stage
      setSelectedDeals((prev) => {
        const next = new Set(prev);
        stageDeals.forEach((d: any) => {
          next.delete(d.id);
        });
        return next;
      });
    } else {
      // Otherwise, select all deals in this stage
      setSelectedDeals((prev) => {
        const next = new Set(prev);
        stageDeals.forEach((d: any) => {
          next.add(d.id);
        });
        return next;
      });
    }
  };

  const handleBulkMove = (stageId: string, pipelineId?: string) => {
    if (selectedDeals.size === 0) return;
    bulkMove.mutate({ dealIds: Array.from(selectedDeals), stageId, pipelineId });
  };

  const handleBulkDelete = () => {
    if (selectedDeals.size === 0) return;
    if (confirm(t("crm.deleteDealsConfirm", { count: selectedDeals.size }))) {
      bulkDelete.mutate(Array.from(selectedDeals));
    }
  };

  // Get selected deals data
  const getSelectedDealsData = () => {
    const selectedDealsData: any[] = [];
    for (const [stageId, deals] of Object.entries(loadedDealsByStage)) {
      for (const deal of deals) {
        if (selectedDeals.has(deal.id)) {
          selectedDealsData.push(deal);
        }
      }
    }
    // Also check board.data.deals if needed
    if (selectedDealsData.length === 0 && board.data?.deals) {
      for (const deal of board.data.deals) {
        if (selectedDeals.has(deal.id)) {
          selectedDealsData.push(deal);
        }
      }
    }
    return selectedDealsData;
  };

  // Prepare data for export with selected columns
  const prepareExportData = (deals: any[]) => {
    const stageNames = new Map<string, string>();
    stages.forEach((stage: any) => {
      stageNames.set(stage.id, stage.name);
    });

    const columnMap: Record<string, (deal: any, contact: any, stage: string) => string> = {
      title: (deal) => deal.title || "",
      contactName: (deal, contact) => contact?.name || "",
      email: (deal, contact) => contact?.email || "",
      phone: (deal, contact) => contact?.phone || "",
      address: (deal, contact) => deal.address || contact?.street || "",
      city: (deal, contact) => deal.city || contact?.city || "",
      stage: (deal, contact, stage) => stage || "",
      value: (deal) => deal.value?.toString() || "",
      currency: (deal) => deal.currency || "",
      width: (deal) => deal.width?.toString() || "",
      height: (deal) => deal.height?.toString() || "",
      color: (deal) => deal.color || "",
      surface: (deal) => deal.surface || "",
      emboss: (deal) => deal.emboss || "",
      createdAt: (deal) => deal.createdAt || deal.created_at || "",
      closingDate: (deal) => deal.closingDate || deal.closing_date || "",
    };

    const selectedColumnsArray = Array.from(selectedColumns).filter(col => columnMap[col]);
    const headers = selectedColumnsArray.map(col => {
      const c = availableColumns.find(cc => cc.id === col);
      return c ? t(c.labelKey) : col;
    });
    
    const rows = deals.map((deal: any) => {
      const contact = deal.contacts || deal.contact;
      const stage = stageNames.get(deal.stageId || deal.stage_id) || "";
      return selectedColumnsArray.map(col => {
        const value = columnMap[col](deal, contact, stage);
        return value;
      });
    });

    return { headers, rows };
  };

  const handleExportCSV = async () => {
    if (selectedDeals.size === 0) {
      setShowExportDialog(true);
      return;
    }
    await doExport("csv");
  };

  const doExport = async (format: "csv" | "excel" | "pdf") => {
    const selectedDealsData = getSelectedDealsData();
    if (selectedDealsData.length === 0) {
      alert(t("crm.noDealsSelected"));
      return;
    }

    const { headers, rows } = prepareExportData(selectedDealsData);

    const d = new Date();
    const localDate = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    const fileName = `deal_export_${localDate}`;

    if (format === "csv") {
      const csv = [headers, ...rows]
        .map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(","))
        .join("\n");
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      downloadBlob(blob, `${fileName}.csv`);
    } else if (format === "excel") {
      const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Deals");
      XLSX.writeFile(wb, `${fileName}.xlsx`);
    } else if (format === "pdf") {
      try {
        const pdfDoc = await PDFDocument.create();
        let currentPage = pdfDoc.addPage([595, 842]); // A4 size
        const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
        const boldFont = await pdfDoc.embedFont(StandardFonts.HelveticaBold);

        let y = 820;
        const fontSize = 10;
        const rowHeight = 15;
        const margin = 40;
        const colWidths = [100, 80, 80, 80, 100, 80, 80, 60, 60];
        let x = margin;

        // Headers
        x = margin;
        headers.forEach((header, idx) => {
          if (x < 550) {
            currentPage.drawText(header, {
              x,
              y,
              size: fontSize,
              font: boldFont,
              color: rgb(0, 0, 0),
            });
            x += colWidths[idx % colWidths.length] || 80;
          }
        });
        y -= rowHeight;
        currentPage.drawLine({
          start: { x: margin, y },
          end: { x: 555, y },
          thickness: 1,
          color: rgb(0, 0, 0),
        });
        y -= rowHeight;

        // Rows
        rows.forEach((row) => {
          if (y < 60) {
            currentPage = pdfDoc.addPage([595, 842]);
            y = 820;
          }
          x = margin;
          row.forEach((cell, idx) => {
            if (x < 550) {
              currentPage.drawText(String(cell).substring(0, 30), {
                x,
                y,
                size: fontSize - 1,
                font,
                color: rgb(0, 0, 0),
              });
              x += colWidths[idx % colWidths.length] || 80;
            }
          });
          y -= rowHeight;
        });

        const pdfBytes = await pdfDoc.save();
        // Convert Uint8Array to Blob
        const blob = new Blob([new Uint8Array(pdfBytes)], { type: "application/pdf" });
        downloadBlob(blob, `${fileName}.pdf`);
      } catch (error) {
        logger.error("PDF export error:", error);
        alert(t("crm.exportPdfError"));
      }
    }

    setShowExportDialog(false);
  };

  const downloadBlob = (blob: Blob, fileName: string) => {
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", fileName);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const create = useMutation({
    mutationFn: async (params?: { pipelineId?: string | null }) => {
      const titleValid = !!title.trim();
      const contactValid =
        contactId === "new" ? !!newContactName.trim() : !!contactId;
      if (!titleValid || !contactValid)
        throw new Error(t("crm.requiredDealAndContact"));

      // Create contact if needed
      let cid = contactId;
      if (cid === "new") {
        try {
          const url = apiUrl("/api/crm/contacts");
          logger.debug('Creating contact at:', url);
          const rc = await fetch(url, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
            body: JSON.stringify({
              name: newContactName,
              email: newContactEmail || undefined,
              phone: newContactPhone || undefined,
              city: newContactCity || undefined,
              street: newContactStreet || undefined,
            }),
          });
          if (!rc.ok) throw new Error(`Kontakt létrehozása sikertelen: ${rc.status}`);
          const c = await rc.json();
          cid = c.id;
          qc.invalidateQueries({ queryKey: ["crm-contacts"] });
        } catch (err) {
          logger.error('Contact creation error:', err);
          throw new Error(`Kontakt hiba: ${err instanceof Error ? err.message : 'Fetch failed'}`);
        }
      }

      try {
        const defaultStageId =
          (stages || []).find((s: any) => s.name === "Új megkeresés")
            ?.id || (stages?.[0]?.id as string | undefined);
        const sid = stageId || defaultStageId;
        const body: Record<string, unknown> = {
          title: title.trim(),
          stageId: sid,
          contactId: cid,
          closingDate: closingDate || new Date().toISOString().slice(0, 10),
        };
        if (params?.pipelineId) {
          body.pipelineId = params.pipelineId;
        }
        const url = apiUrl("/api/crm/deals");
        logger.debug('Creating deal at:', url);
        const r = await fetch(url, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify(body),
        });
        if (!r.ok) throw new Error(`${t("crm.createFailed")}: ${r.status}`);
        return r.json();
      } catch (err) {
        logger.error('Deal creation error:', err);
        throw new Error(`${t("crm.createFailed")}: ${err instanceof Error ? err.message : 'Fetch failed'}`);
      }
    },
    onSuccess: () => {
      setOpen(false);
      setTitle("");
      setStageId("");
      setContactId("");
      setNewContactName("");
      setNewContactEmail("");
      setNewContactPhone("");
      setNewContactCity("");
      setNewContactStreet("");
      setClosingDate(new Date().toISOString().slice(0, 10));
      setDealSiteCity("");
      setDealSiteAddress("");
      setTouchedFields(new Set()); // Reset validation state
      qc.invalidateQueries({ queryKey: ["crm-board"] });
      qc.invalidateQueries({ queryKey: ["crm-deals"] });
      qc.refetchQueries({ queryKey: ["crm-board"] });
    },
  });

  // Initialize limits and totals for stages from board data
  // CRITICAL: This useEffect MUST be called before any early returns
  // to comply with React Rules of Hooks
  useEffect(() => {
    // Calculate values inside useEffect to avoid dependency on computed values
    // Include pipeline_id so we re-sync when any deal's pipeline changes (e.g. after assigning sub-pipeline on deal page)
    const boardDealsHash = board.data?.deals
      ? board.data.deals.map((d: any) => (d.pipelineId ?? d.pipeline_id ?? "") + ":" + d.id).join("|")
      : "";
    const boardDealsCount = board.data?.deals?.length || 0;
    const boardStagesCount = board.data?.stages?.length || 0;
    
    // Calculate effectiveSubPipelineId inside useEffect
    const subPipelines = board.data?.pipelines?.filter((p: any) => p.parent_pipeline_id) || [];
    const defaultSubPipelineId = subPipelines.length > 0 ? subPipelines[0].id : null;
    const effectiveSubPipelineId = selectedSubPipelineId || defaultSubPipelineId;
    
    if (!board.data?.deals || !board.data?.stages || boardStagesCount === 0) return;

    // Check if board data actually changed (not just count, but content)
    const currentBoardData = {
      dealsCount: boardDealsCount,
      stagesCount: boardStagesCount,
      dealsHash: boardDealsHash,
    };
    
    const hasBoardDataChanged = !prevBoardDataRef.current || 
      prevBoardDataRef.current.dealsCount !== currentBoardData.dealsCount ||
      prevBoardDataRef.current.stagesCount !== currentBoardData.stagesCount ||
      prevBoardDataRef.current.dealsHash !== currentBoardData.dealsHash;
    
    // Reset when sub-pipeline changes
    const subPipelineChanged = effectiveSubPipelineId !== prevSubPipelineRef.current;
    
    if (subPipelineChanged) {
      setLoadedDealsByStage({});
      setDealsLimitPerStage({});
      prevSubPipelineRef.current = effectiveSubPipelineId;
    }

    // Re-sync from board when data or sub-pipeline changed (hash includes pipeline_id for first 200 deals so many pipeline changes are detected; we still skip when hash/count unchanged to avoid redundant setState)
    if (!hasBoardDataChanged && !subPipelineChanged) {
      return;
    }

    prevBoardDataRef.current = currentBoardData;
    
    // When default sort, list and totals are loaded from API (sorting useEffect). Don't overwrite with board (avoids cache anomaly).
    if (sortBy === "createdAt" && sortOrder === "desc") {
      return;
    }
    
    // Filter stages inside useEffect to avoid dependency on computed filteredStages
    const stagesToProcess = !effectiveSubPipelineId
      ? board.data.stages
      : board.data.stages.filter((s: any) => s.pipeline_id === effectiveSubPipelineId || s.pipelineId === effectiveSubPipelineId);
    
    const filteredDeals = effectiveSubPipelineId
      ? board.data.deals.filter((d: any) => d.pipelineId === effectiveSubPipelineId || d.pipeline_id === effectiveSubPipelineId)
      : board.data.deals;

    const newTotals: Record<string, number> = {};
    const newLoaded: Record<string, any[]> = {};
    const newLimits: Record<string, number> = {};
    
    // Track which stages we're processing to avoid duplicates
    const processedStages = new Set<string>();
    
    stagesToProcess.forEach((stage: any) => {
      if (processedStages.has(stage.id)) return;
      processedStages.add(stage.id);
      
      const stageDeals = filteredDeals.filter((d: any) => d.stageId === stage.id || d.stage_id === stage.id);
      newTotals[stage.id] = stageDeals.length;
      newLoaded[stage.id] = stageDeals;
      
      if (!(stage.id in dealsLimitPerStage)) {
        newLimits[stage.id] = INITIAL_DEALS_PER_STAGE;
      }
    });

    // Update loaded deals - only if changed
    setLoadedDealsByStage((prev) => {
      // Check if anything actually changed
      let hasChanges = false;
      for (const stageId of Object.keys(newLoaded)) {
        const prevDeals = prev[stageId] || [];
        const newDeals = newLoaded[stageId] || [];
        if (prevDeals.length !== newDeals.length) {
          hasChanges = true;
          break;
        }
        const prevIds = new Set(prevDeals.map((d: any) => d.id));
        const newIds = new Set(newDeals.map((d: any) => d.id));
        if (prevIds.size !== newIds.size) {
          hasChanges = true;
          break;
        }
        for (const id of newIds) {
          if (!prevIds.has(id)) {
            hasChanges = true;
            break;
          }
        }
        if (hasChanges) break;
      }
      
      if (!hasChanges) {
        // Check if any stages were removed
        const prevStageIds = new Set(Object.keys(prev));
        const newStageIds = new Set(Object.keys(newLoaded));
        if (prevStageIds.size !== newStageIds.size) {
          hasChanges = true;
        } else {
          for (const id of newStageIds) {
            if (!prevStageIds.has(id)) {
              hasChanges = true;
              break;
            }
          }
        }
      }
      
      if (!hasChanges) {
        return prev; // No changes, return prev to avoid re-render
      }
      
      // Merge: preserve lazy loaded deals that are sorted by backend
      // CRITICAL: Don't overwrite lazy loaded deals with board.data.deals,
      // because board.data.deals is sorted by createdAt, not by user's sortBy/sortOrder preference
      // Only use board.data.deals if there are no lazy loaded deals yet
      // If deals were moved between stages, we need to update the lazy loaded deals accordingly
      const updated: Record<string, any[]> = {};
      
      // First, check if any deals were actually moved between stages
      // by comparing deal IDs in each stage
      const dealsMoved = new Map<string, Set<string>>(); // stageId -> set of deal IDs that should be there
      for (const stageId of Object.keys(newLoaded)) {
        const newDealIds = new Set(newLoaded[stageId].map((d: any) => d.id));
        const prevDealIds = new Set((prev[stageId] || []).map((d: any) => d.id));
        
        // Check if any deals were added or removed from this stage
        const added = [...newDealIds].filter(id => !prevDealIds.has(id));
        const removed = [...prevDealIds].filter(id => !newDealIds.has(id));
        
        if (added.length > 0 || removed.length > 0) {
          dealsMoved.set(stageId, newDealIds);
        }
      }
      
      for (const stageId of Object.keys(newLoaded)) {
        // If we have lazy loaded deals for this stage, keep them (they're sorted correctly by backend)
        // Only use board.data.deals if we don't have lazy loaded deals yet
        if (prev[stageId] && prev[stageId].length > 0) {
          // We have lazy loaded deals - check if any deals were moved
          if (dealsMoved.has(stageId)) {
            // Deals were moved - update the list but preserve sorting
            // Remove deals that are no longer in this stage
            const newDealIds = dealsMoved.get(stageId)!;
            const filteredDeals = prev[stageId].filter((d: any) => newDealIds.has(d.id));
            
            // Add new deals that were moved to this stage (from board.data.deals)
            const existingIds = new Set(filteredDeals.map((d: any) => d.id));
            const newDeals = newLoaded[stageId].filter((d: any) => !existingIds.has(d.id));
            
            // Combine and maintain sort order (deals from backend are already sorted)
            updated[stageId] = [...filteredDeals, ...newDeals];
          } else {
            // No deals moved - keep the lazy loaded deals as they are (sorted by backend)
            updated[stageId] = prev[stageId];
          }
        } else {
          // No lazy loaded deals yet
          // CRITICAL: If sortBy/sortOrder is set (not default), don't use board.data.deals
          // because it's only sorted by createdAt. Instead, we'll load sorted deals from backend
          // This ensures correct sorting even on page refresh
          if (sortBy !== "createdAt" || sortOrder !== "desc") {
            // User has custom sorting - don't use board.data.deals, it will be loaded by the sorting useEffect
            // Leave it empty for now, the sorting useEffect will load it
            updated[stageId] = [];
          } else {
            // Default sorting (createdAt desc) - board.data.deals is already sorted correctly
            updated[stageId] = newLoaded[stageId];
          }
        }
      }
      
      // Also include any stages that are in prev but not in newLoaded
      for (const stageId of Object.keys(prev)) {
        if (!updated[stageId]) {
          updated[stageId] = prev[stageId];
        }
      }
      
      return updated;
    });
    
    // Only update limits if there are new ones
    if (Object.keys(newLimits).length > 0) {
      setDealsLimitPerStage((prev) => {
        const hasChanges = Object.keys(newLimits).some(key => prev[key] !== newLimits[key]);
        return hasChanges ? { ...prev, ...newLimits } : prev;
      });
    }
    
    // Only update totals if changed
    setTotalDealsByStage((prev) => {
      const hasChanges = Object.keys(newTotals).some(key => prev[key] !== newTotals[key]);
      return hasChanges ? { ...prev, ...newTotals } : prev;
    });
  }, [board.data, selectedSubPipelineId, dealsLimitPerStage, sortBy, sortOrder]); // Use board.data and selectedSubPipelineId directly

  // Reload deals when sorting changes (to get ALL deals sorted by backend).
  // Uses AbortController so that when deps change mid-flight, stale responses
  // from the previous cycle are discarded instead of overwriting fresh state.
  useEffect(() => {
    if (!board.data?.stages || !board.data?.deals) return;

    const controller = new AbortController();

    const subPipelines = board.data.pipelines?.filter((p: any) => p.parent_pipeline_id) || [];
    const defaultSubPipelineId = subPipelines.length > 0 ? subPipelines[0].id : null;
    const effectiveSubPipelineId = selectedSubPipelineId || defaultSubPipelineId;

    const stagesToProcess = !effectiveSubPipelineId
      ? board.data.stages
      : board.data.stages.filter((s: any) => s.pipeline_id === effectiveSubPipelineId || s.pipelineId === effectiveSubPipelineId);

    const stageIdsToLoad = stagesToProcess.map((s: any) => s.id as string);

    setLoadingStages((prev) => {
      const next = new Set(prev);
      stageIdsToLoad.forEach((id) => next.add(id));
      return next;
    });

    Promise.all(
      stageIdsToLoad.map(async (stageId) => {
        const pipelineId = effectiveSubPipelineId || undefined;
        const params = new URLSearchParams();
        params.set("stageId", stageId);
        params.set("limit", String(INITIAL_DEALS_PER_STAGE));
        params.set("offset", "0");
        params.set("sortBy", sortBy);
        params.set("sortOrder", sortOrder);
        if (pipelineId) params.set("pipelineId", pipelineId);

        const response = await fetch(apiUrl(`/api/crm/deals?${params.toString()}`), {
          credentials: "include",
          signal: controller.signal,
        });

        if (!response.ok) throw new Error("Failed to reload deals");
        const { deals, total } = await response.json();
        return { stageId, deals: deals as Deal[], total: total as number };
      }),
    )
      .then((results) => {
        if (controller.signal.aborted) return;

        setLoadedDealsByStage((prev) => {
          const next = { ...prev };
          results.forEach((r) => { next[r.stageId] = r.deals; });
          return next;
        });
        setDealsLimitPerStage((prev) => {
          const next = { ...prev };
          results.forEach((r) => { next[r.stageId] = r.deals.length; });
          return next;
        });
        setTotalDealsByStage((prev) => {
          const next = { ...prev };
          results.forEach((r) => { next[r.stageId] = r.total; });
          return next;
        });
      })
      .catch((err) => {
        if (err instanceof DOMException && err.name === "AbortError") return;
        logger.error("Error reloading deals:", err);
      })
      .finally(() => {
        if (controller.signal.aborted) return;
        setLoadingStages((prev) => {
          const next = new Set(prev);
          stageIdsToLoad.forEach((id) => next.delete(id));
          return next;
        });
      });

    return () => controller.abort();
  }, [sortBy, sortOrder, board.data, selectedSubPipelineId]);

  if (board.isLoading || contacts.isLoading)
    return (
      <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
        <Loading message={t("crm.loadingData")} />
      </div>
    );
  if (board.isError || contacts.isError)
    return (
      <div className="p-6 text-red-600">
        <div className="mb-3">
          {t("crm.loadError")}
        </div>
        {board.isError && (
          <div className="text-sm">{(board.error as any)?.message}</div>
        )}
        {contacts.isError && (
          <div className="text-sm">{(contacts.error as any)?.message}</div>
        )}
      </div>
    );

  if (!board.data) return <div className="p-6">{t("crm.noData")}</div>;

  // Filter sub-pipelines (those with parent_pipeline_id)
  // Compute directly without useMemo to avoid React error #310
  const subPipelines = board.data.pipelines.filter((p: any) => p.parent_pipeline_id);
  
  // Calculate default sub-pipeline ID (first one if available)
  // Compute directly without useMemo to avoid React error #310
  const defaultSubPipelineId = subPipelines.length > 0 ? subPipelines[0].id : null;
  
  // Use defaultSubPipelineId if selectedSubPipelineId is not set
  const effectiveSubPipelineId = selectedSubPipelineId || defaultSubPipelineId;

  // Filter stages by selected sub-pipeline
  // Compute directly without useMemo to avoid React error #310
  const filteredStages = !board.data?.stages
    ? []
    : !effectiveSubPipelineId
    ? board.data.stages
    : board.data.stages.filter((s: any) => s.pipeline_id === effectiveSubPipelineId || s.pipelineId === effectiveSubPipelineId);

  // Sort stages by order (handle both order and order_index field names)
  const stages = [...filteredStages].sort((a: any, b: any) => {
    const orderA = a.order ?? a.order_index ?? a.orderIndex ?? 0;
    const orderB = b.order ?? b.order_index ?? b.orderIndex ?? 0;
    return orderA - orderB;
  });
  
  // Handle load more for a specific stage
  const handleLoadMore = async (stageId: string) => {
    if (loadingStages.has(stageId)) return;
    
    setLoadingStages((prev) => new Set(prev).add(stageId));
    
    try {
      const currentLimit = dealsLimitPerStage[stageId] || INITIAL_DEALS_PER_STAGE;
      const pipelineId = effectiveSubPipelineId || undefined;
      
      // Build query string manually
      const params = new URLSearchParams();
      params.set("stageId", stageId);
      params.set("limit", String(DEALS_LOAD_INCREMENT));
      params.set("offset", String(currentLimit));
      params.set("sortBy", sortBy);
      params.set("sortOrder", sortOrder);
      if (pipelineId) {
        params.set("pipelineId", pipelineId);
      }
      
      const url = `${apiUrl("/api/crm/deals")}?${params.toString()}`;
      
      const response = await fetch(url, {
        credentials: "include",
      });
      
      if (!response.ok) {
        // If 400 or 404, it means no more deals or invalid request - don't throw, just stop loading
        if (response.status === 400 || response.status === 404) {
          logger.warn("No more deals to load or invalid request for stage:", stageId);
          setTotalDealsByStage((prev) => ({
            ...prev,
            [stageId]: (prev[stageId] || 0),
          }));
          return;
        }
        throw new Error("Failed to load more deals");
      }
      
      const { deals: newDeals, total } = await response.json();
      
      // If no deals returned, stop trying to load more
      if (!newDeals || newDeals.length === 0) {
        setTotalDealsByStage((prev) => ({
          ...prev,
          [stageId]: (prev[stageId] || 0),
        }));
        return;
      }
      
      // Append new deals to existing deals for this stage
      setLoadedDealsByStage((prev) => ({
        ...prev,
        [stageId]: [...(prev[stageId] || []), ...newDeals],
      }));
      
      setDealsLimitPerStage((prev) => ({
        ...prev,
        [stageId]: currentLimit + newDeals.length,
      }));
      
      setTotalDealsByStage((prev) => ({
        ...prev,
        [stageId]: total,
      }));
    } catch (error) {
      logger.error("Error loading more deals:", error);
    } finally {
      setLoadingStages((prev) => {
        const next = new Set(prev);
        next.delete(stageId);
        return next;
      });
    }
  };
  
  // Filter deals by stage from loaded deals (already sorted by backend)
  // NOTE: Sorting is done on the backend via /api/crm/deals endpoint
  // This ensures ALL deals (even 2000+) are sorted correctly, not just the loaded ones
  const dealsByStage = new Map<string, any[]>();
  
  if (stages.length > 0) {
    stages.forEach((stage: any) => {
      // Get deals for this stage (already sorted by backend)
      let stageDeals = loadedDealsByStage[stage.id] || [];
      if (showOverdueClosingOnly) {
        stageDeals = stageDeals.filter((d: any) => dealIsClosingOverdue(d));
      }

      // Apply limit for display (deals are already sorted by backend)
      const limit = dealsLimitPerStage[stage.id] || INITIAL_DEALS_PER_STAGE;
      dealsByStage.set(stage.id, stageDeals.slice(0, limit));
    });
  }

  const pipelineFilteredBoardDeals = effectiveSubPipelineId
    ? board.data.deals.filter(
        (d: any) =>
          d.pipelineId === effectiveSubPipelineId || d.pipeline_id === effectiveSubPipelineId,
      )
    : board.data.deals;

  const overdueClosingCountByStage: Record<string, number> = {};
  stages.forEach((s: any) => {
    overdueClosingCountByStage[s.id] = pipelineFilteredBoardDeals.filter(
      (d: any) => (d.stageId === s.id || d.stage_id === s.id) && dealIsClosingOverdue(d),
    ).length;
  });

  return (
    <div className={cn("h-full flex flex-col overflow-hidden", isMobile && "overflow-x-hidden max-w-full")}>
      {/* Header container - fixed at top, always visible, not scrollable */}
      <div className="flex-shrink-0 h-[60px] px-4 py-4 relative overflow-hidden bg-background border-b border-border z-20">
        {/* Normal Header */}
        <div
          className={cn(
            "absolute inset-0 flex items-center justify-between px-2 transition-all duration-300 ease-in-out",
            selectionMode
              ? "opacity-0 translate-y-[-100%] pointer-events-none"
              : "opacity-100 translate-y-0"
          )}
        >
        {isMobile ? (
          <PageHeader
            sortBy={sortBy}
            sortOrder={sortOrder}
            onSortByChange={setSortBy}
            onSortOrderChange={setSortOrder}
            subPipelines={subPipelines.map((p: any) => ({ id: p.id, name: getTranslatedName(p, lang) }))}
            selectedSubPipelineId={effectiveSubPipelineId}
            onSubPipelineChange={setSelectedSubPipelineId}
            overdueFilterActive={showOverdueClosingOnly}
            onOverdueFilterToggle={() => setShowOverdueClosingOnly((v) => !v)}
            onSearchClick={() => {
              // Open global search - this will be handled by the Header component
              const searchButton = document.querySelector('[data-search-trigger]') as HTMLElement;
              if (searchButton) {
                searchButton.click();
              }
            }}
            className="absolute inset-0 border-0"
          />
        ) : (
          <PageTitleBar
            className="w-full min-w-0 border-0"
            rowClassName="h-full min-h-0 items-center px-2"
            title={t("crm.title")}
            titleSuffix={
              <Button
                type="button"
                variant={showOverdueClosingOnly ? "default" : "outline"}
                size="sm"
                className="shrink-0 gap-1.5"
                title={t("crm.filterOverdueOnlyHint")}
                aria-pressed={showOverdueClosingOnly}
                onClick={() => setShowOverdueClosingOnly((v) => !v)}
              >
                <CalendarClock className="h-4 w-4" />
                <span className="hidden sm:inline">{t("crm.filterOverdueOnly")}</span>
              </Button>
            }
            right={
              <div className="flex items-center gap-2 sm:gap-4">
                <SortControls
                  sortBy={sortBy}
                  sortOrder={sortOrder}
                  onSortByChange={setSortBy}
                  onSortOrderChange={setSortOrder}
                />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setSelectionModeEnabled(!selectionModeEnabled);
                    if (selectionModeEnabled) {
                      setSelectedDeals(new Set());
                    }
                  }}
                >
                  {selectionModeEnabled ? t("crm.selectionOff") : t("common.selectTrigger")}
                </Button>
                <Button onClick={() => setOpen(true)} size="sm">
                  <Plus className="mr-2 h-4 w-4" />
                  {t("crm.newDeal")}
                </Button>
              </div>
            }
          />
        )}
        </div>

        {/* Toolbar - slides up when selection mode is enabled */}
        <div
          className={cn(
            "absolute inset-0 flex items-center justify-between px-2 transition-all duration-300 ease-in-out",
            selectionModeEnabled
              ? "opacity-100 translate-y-0"
              : "opacity-0 translate-y-[100%] pointer-events-none"
          )}
        >
          {isMobile ? (
            <>
              <div className={cn("flex items-center gap-4", "flex-wrap gap-2 w-full")}>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowBulkOwnerDialog(true)}
                  disabled={selectedDeals.size === 0}
                  className="flex-1 min-w-0"
                >
                  {t("crm.owner")}
                </Button>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="sm" disabled={selectedDeals.size === 0} className="flex-1 min-w-0">
                      {t("crm.more")} <MoreHorizontal className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start">
                    <DropdownMenuItem onClick={() => setShowExportDialog(true)}>
                      <Download className="mr-2 h-4 w-4" />
                      {t("crm.export")}…
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => setShowBulkMoveDialog(true)}>
                      <MoveRight className="mr-2 h-4 w-4" />
                      {t("crm.move")}
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={handleBulkDelete}
                      className="text-destructive focus:text-destructive"
                    >
                      <Trash2 className="mr-2 h-4 w-4" />
                      {t("crm.delete")}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
                <span className="text-sm font-medium">
                  {selectedDeals.size} kiválasztva
                </span>
                <button
                  onClick={() => {
                    const allDealIds = new Set<string>();
                    stages.forEach((stage: any) => {
                      const stageDeals = dealsByStage.get(stage.id) || [];
                      stageDeals.forEach((d: any) => {
                        allDealIds.add(d.id);
                      });
                    });
                    setSelectedDeals(allDealIds);
                  }}
                  className="text-sm text-primary hover:underline"
                >
                  {t("crm.selectAllDealsInView")}
                </button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-9 w-9 shrink-0 p-0"
                  aria-label={t("crm.clearSelection")}
                  onClick={() => {
                    setSelectedDeals(new Set());
                    setSelectionModeEnabled(false);
                  }}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowBulkMoveDialog(true)}
                  disabled={bulkMove.isPending}
                >
                  <MoveRight className="mr-2 h-4 w-4" />
                  {t("crm.move")}
                </Button>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={handleBulkDelete}
                  disabled={bulkDelete.isPending}
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  {t("crm.delete")}
                </Button>
              </div>
            </>
          ) : (
            <SelectionToolbarLayout
              className="px-2"
              selectedCount={selectedDeals.size}
              onClose={() => {
                setSelectedDeals(new Set());
                setSelectionModeEnabled(false);
              }}
              onSelectAll={() => {
                const allDealIds = new Set<string>();
                stages.forEach((stage: any) => {
                  const stageDeals = dealsByStage.get(stage.id) || [];
                  stageDeals.forEach((d: any) => {
                    allDealIds.add(d.id);
                  });
                });
                setSelectedDeals(allDealIds);
              }}
              selectAllDisabled={stages.length === 0}
              bulkActions={
                <>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowBulkOwnerDialog(true)}
                    disabled={selectedDeals.size === 0}
                  >
                    {t("crm.changeOwner")}
                  </Button>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" size="sm" disabled={selectedDeals.size === 0}>
                        {t("crm.more")} <MoreHorizontal className="ml-1 h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start">
                      <DropdownMenuItem onClick={() => setShowExportDialog(true)}>
                        <Download className="mr-2 h-4 w-4" />
                        {t("crm.export")}…
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => setShowBulkMoveDialog(true)}>
                        <MoveRight className="mr-2 h-4 w-4" />
                        {t("crm.move")}
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={handleBulkDelete}
                        className="text-destructive focus:text-destructive"
                      >
                        <Trash2 className="mr-2 h-4 w-4" />
                        {t("crm.delete")}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </>
              }
              rightActions={
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowBulkMoveDialog(true)}
                  disabled={selectedDeals.size === 0 || bulkMove.isPending}
                >
                  <MoveRight className="mr-2 h-4 w-4" />
                  {t("crm.move")}
                </Button>
              }
              onDelete={handleBulkDelete}
              deleteDisabled={selectedDeals.size === 0}
              deletePending={bulkDelete.isPending}
            />
          )}
        </div>
      </div>

      {/* Create Deal Sheet */}
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent
          ref={setNewDealSheetHost}
          side={isMobile ? "bottom" : "right"}
          className={cn(
            isMobile
              ? "rounded-t-2xl pb-[env(safe-area-inset-bottom)] max-h-[85dvh]"
              : "sm:max-w-md",
            "flex flex-col gap-0 p-0",
          )}
          hideClose
          onOpenAutoFocus={(e) => e.preventDefault()}
          onPointerDownOutside={preventDismissWhenPointerOnRadixPopper}
          onInteractOutside={preventDismissWhenPointerOnRadixPopper}
          onFocusOutside={preventDismissWhenPointerOnRadixPopper}
        >
          <SheetHeader
            className={cn(
              "flex-shrink-0 space-y-2 border-b border-border px-6 text-left",
              isMobile ? "pt-3 pb-3" : "py-4",
            )}
          >
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0 flex-1 space-y-1 pr-2">
                <SheetTitle className="text-lg font-semibold leading-tight">
                  {t("crm.newDeal")}
                </SheetTitle>
                <SheetDescription className="text-sm leading-snug">
                  {t("crm.newDealSubtitle")}
                </SheetDescription>
              </div>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() => setOpen(false)}
                className="h-9 w-9 shrink-0 rounded-full"
              >
                <X className="h-4 w-4" />
                <span className="sr-only">{t("common.close")}</span>
              </Button>
            </div>
          </SheetHeader>

          <div
            className={cn(
              "flex-1 space-y-5 overflow-y-auto overscroll-contain px-6",
              isMobile ? "py-4" : "py-5",
            )}
          >
            {stageId ? (() => {
              const stageName = stages.find((s: Stage) => s.id === stageId)?.name?.trim();
              return stageName ? (
                <p className="rounded-lg border border-border bg-muted/50 px-3 py-2 text-xs text-muted-foreground">
                  {t("crm.newDealTargetStage", { stage: stageName })}
                </p>
              ) : null;
            })() : null}

            <div className="space-y-2">
              <Label htmlFor="new-deal-contact">
                {t("crm.contact")} <span className="text-destructive">*</span>
              </Label>
              <ContactSelectCombobox
                id="new-deal-contact"
                portalContainer={newDealSheetHost}
                value={contactId}
                onValueChange={(v) => {
                  setContactId(v);
                  setTouchedFields((prev) => new Set(prev).add("contactId"));
                }}
                onBlur={() => setTouchedFields((prev) => new Set(prev).add("contactId"))}
                contacts={contacts.data ?? []}
                disabled={contacts.isLoading}
                loading={contacts.isLoading}
                loadingLabel={t("crm.loadingContacts")}
                noneLabel={t("crm.contactNone")}
                newContactLabel={t("crm.contactNew")}
                searchPlaceholder={t("deal.searchContacts")}
                emptyLabel={t("deal.noResults")}
                triggerClassName={cn(
                  contacts.isLoading && "cursor-not-allowed opacity-50",
                  touchedFields.has("contactId") &&
                    !contactId &&
                    !(contactId === "new" && newContactName.trim()) &&
                    "border-destructive",
                )}
                aria-invalid={
                  !!(touchedFields.has("contactId") && !contactId && !(contactId === "new" && newContactName.trim()))
                }
              />
              {touchedFields.has("contactId") &&
                !contactId &&
                !(contactId === "new" && newContactName.trim()) && (
                  <p className="text-sm font-medium text-destructive">{t("crm.contactRequired")}</p>
                )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="new-deal-title">
                {t("crm.dealName")} <span className="text-destructive">*</span>
              </Label>
              <Input
                id="new-deal-title"
                value={title}
                onChange={(e) => {
                  setTitle(e.target.value);
                  setTouchedFields((prev) => new Set(prev).add("title"));
                }}
                onBlur={() => setTouchedFields((prev) => new Set(prev).add("title"))}
                placeholder={t("crm.dealTitleExample")}
                className={cn(touchedFields.has("title") && !title.trim() && "border-destructive")}
                aria-invalid={touchedFields.has("title") && !title.trim()}
              />
              {touchedFields.has("title") && !title.trim() && (
                <p className="text-sm font-medium text-destructive">{t("crm.dealNameRequired")}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="new-deal-closing">{t("crm.closingDate")}</Label>
              <DatePickerPopover
                id="new-deal-closing"
                value={closingDate}
                onChange={setClosingDate}
              />
            </div>

            {contactId === "new" ? (
              <>
                <Separator />
                <div className="space-y-3">
                  <p className="text-sm font-medium text-foreground">{t("crm.contactNew")}</p>
                  <div className="space-y-2">
                    <Label htmlFor="new-deal-contact-name">
                      {t("crm.colName")} <span className="text-destructive">*</span>
                    </Label>
                    <Input
                      id="new-deal-contact-name"
                      value={newContactName}
                      onChange={(e) => {
                        setNewContactName(e.target.value);
                        setTouchedFields((prev) => new Set(prev).add("newContactName"));
                      }}
                      onBlur={() => setTouchedFields((prev) => new Set(prev).add("newContactName"))}
                      placeholder={t("crm.namePlaceholder")}
                      className={cn(
                        touchedFields.has("newContactName") && !newContactName.trim() && "border-destructive",
                      )}
                    />
                    {touchedFields.has("newContactName") && !newContactName.trim() && (
                      <p className="text-sm font-medium text-destructive">{t("crm.nameRequired")}</p>
                    )}
                  </div>
                  <Input
                    value={newContactEmail}
                    onChange={(e) => setNewContactEmail(e.target.value)}
                    placeholder={t("crm.emailPlaceholder")}
                    autoComplete="email"
                  />
                  <Input
                    value={newContactPhone}
                    onChange={(e) => setNewContactPhone(e.target.value)}
                    placeholder={t("crm.phonePlaceholder")}
                    autoComplete="tel"
                  />
                  <Input
                    value={newContactCity}
                    onChange={(e) => setNewContactCity(e.target.value)}
                    placeholder={t("crm.cityPlaceholder")}
                    autoComplete="address-level2"
                  />
                  <Input
                    value={newContactStreet}
                    onChange={(e) => setNewContactStreet(e.target.value)}
                    placeholder={t("crm.streetPlaceholder")}
                    autoComplete="street-address"
                  />
                </div>
              </>
            ) : null}

            {contactId && contactId !== "new" ? (
              <>
                <Separator />
                <div className="space-y-3 rounded-lg border border-border bg-muted/50 p-4">
                  <p className="text-sm font-medium text-foreground">{t("crm.clientData")}</p>
                  {(() => {
                    const selectedContact = contacts.data?.find((c: Contact) => c.id === contactId);
                    return selectedContact ? (
                      <div className="space-y-3 text-sm">
                        <div>
                          <p className="mb-1 text-xs text-muted-foreground">{t("crm.colName")}</p>
                          <p className="font-medium">{selectedContact.name}</p>
                        </div>
                        {selectedContact.email ? (
                          <div>
                            <p className="mb-1 text-xs text-muted-foreground">{t("crm.colEmail")}</p>
                            <a href={`mailto:${selectedContact.email}`} className="text-primary hover:underline">
                              {selectedContact.email}
                            </a>
                          </div>
                        ) : null}
                        {selectedContact.phone ? (
                          <div>
                            <p className="mb-1 text-xs text-muted-foreground">{t("crm.colPhone")}</p>
                            <a href={`tel:${selectedContact.phone}`} className="text-primary hover:underline">
                              {selectedContact.phone}
                            </a>
                          </div>
                        ) : null}
                        {selectedContact.city ? (
                          <div>
                            <p className="mb-1 text-xs text-muted-foreground">{t("crm.colCity")}</p>
                            <p>{selectedContact.city}</p>
                          </div>
                        ) : null}
                        {selectedContact.street ? (
                          <div>
                            <p className="mb-1 text-xs text-muted-foreground">{t("crm.colAddress")}</p>
                            <p>{selectedContact.street}</p>
                          </div>
                        ) : null}
                      </div>
                    ) : null;
                  })()}
                </div>
              </>
            ) : null}
          </div>

          <SheetFooter className="flex w-full shrink-0 flex-col gap-3 border-t px-6 py-4 sm:flex-col">
            {create.isError ? (
              <div className="w-full rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-950/30 dark:text-red-400">
                {t("common.error")}: {(create.error as Error)?.message || t("crm.createDealFailed")}
              </div>
            ) : null}
            <div className={cn("flex w-full gap-2", isMobile ? "flex-col-reverse" : "justify-end")}>
              <Button
                type="button"
                variant="outline"
                onClick={() => setOpen(false)}
                className={cn(isMobile && "min-h-[48px] w-full")}
              >
                {t("common.cancel")}
              </Button>
              <Button
                type="button"
                onClick={() => create.mutate({ pipelineId: effectiveSubPipelineId ?? undefined })}
                disabled={
                  !title.trim() ||
                  !(contactId === "new" ? newContactName.trim() : contactId) ||
                  create.isPending
                }
                className={cn(isMobile && "min-h-[48px] w-full")}
              >
                {create.isPending ? t("common.loading") : t("common.save")}
              </Button>
            </div>
          </SheetFooter>
        </SheetContent>
      </Sheet>


      {/* Board Container - flex column to stack board and tabs */}
      <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
        {/* Kanban board - horizontally scrollable with snap-scroll on mobile */}
        <div className={cn(
          "flex-1 flex gap-2 md:gap-4 overflow-y-hidden flex-nowrap min-h-0 overflow-x-auto",
          isMobile 
            ? "py-3 pl-[8%] pr-[8%] snap-x snap-mandatory scrollbar-hide" 
            : "px-2 md:px-2 py-2 md:py-4"
        )}>
          {stages.map((s: any) => {
            const stageDeals = dealsByStage.get(s.id) || [];
            
            return (
            <KanbanColumn
              key={s.id}
              stage={s}
              deals={stageDeals}
              totalDeals={totalDealsByStage[s.id]}
              overdueClosingCount={overdueClosingCountByStage[s.id] ?? 0}
              onDropDeal={handleDropDeal}
              onOpenDeal={(id) => navigate(`/crm/deals/${id}`)}
              onCreateDeal={(sid) => {
                setStageId(sid);
                setOpen(true);
              }}
              collapsed={!!collapsed[s.id]}
              onToggleCollapse={(sid) =>
                setCollapsed((prev) => ({ ...prev, [sid]: !prev[sid] }))
              }
              onLoadMore={() => handleLoadMore(s.id)}
              hasMore={(dealsLimitPerStage[s.id] || INITIAL_DEALS_PER_STAGE) < (totalDealsByStage[s.id] || 0)}
              isLoading={loadingStages.has(s.id)}
              selectionMode={selectionMode}
              selectedDeals={selectedDeals}
              onSelectDeal={handleSelectDeal}
              onSelectStage={handleSelectStage}
            />
            );
          })}
        </div>

        {/* Sub-Pipeline Tabs - Fixed at bottom of board, not scrollable (Desktop only) */}
        {!isMobile && subPipelines.length > 0 && (
          <div className="flex-shrink-0 bg-background border-t z-30" style={{ height: '60px' }}>
            <div className="flex items-center gap-2 px-2 py-2 overflow-x-auto h-full">
              {subPipelines.map((subPipeline: any) => (
                <Button
                  key={subPipeline.id}
                  type="button"
                  size="sm"
                  variant={effectiveSubPipelineId === subPipeline.id ? "default" : "secondary"}
                  onClick={() => setSelectedSubPipelineId(subPipeline.id)}
                  className="whitespace-nowrap"
                >
                  {getTranslatedName(subPipeline, lang)}
                </Button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Bulk Move Dialog */}
      {showBulkMoveDialog && (() => {
        const availableStages = bulkMovePipelineId
          ? (board.data?.stages || []).filter((s: any) => s.pipelineId === bulkMovePipelineId || s.pipeline_id === bulkMovePipelineId)
          : (board.data?.stages || []);

        // safe-area: top inset
        return (
          <>
            {/* safe-area: top inset */}
            <div className="fixed inset-0 bg-black/50 dark:bg-black/70 flex items-center justify-center z-50 pt-[env(safe-area-inset-top)] px-[env(safe-area-inset-right)] pb-[env(safe-area-inset-bottom)] pl-[env(safe-area-inset-left)]">
            <div className="bg-background rounded-lg p-6 max-w-md w-full mx-4">
              <h3 className="text-lg font-semibold mb-4">{t("crm.moveDealsTitle")}</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">{t("crm.pipelineLabel")}</label>
                  <select
                    value={bulkMovePipelineId}
                    onChange={(e) => {
                      setBulkMovePipelineId(e.target.value);
                      setBulkMoveStageId(""); // Reset stage when pipeline changes
                    }}
                    className="w-full px-3 py-2 rounded-lg border border-border bg-background"
                  >
                    <option value="">{t("crm.selectPipelinePlaceholder")}</option>
                    {board.data?.pipelines.map((p: any) => (
                      <option key={p.id} value={p.id}>
                        {p.name}
                      </option>
                    ))}
                  </select>
      </div>
                <div>
                  <label className="block text-sm font-medium mb-2">{t("crm.stageLabelSelect")}</label>
                  <select
                    value={bulkMoveStageId}
                    onChange={(e) => setBulkMoveStageId(e.target.value)}
                    className="w-full px-3 py-2 rounded-lg border border-border bg-background"
                    disabled={!bulkMovePipelineId && availableStages.length === 0}
                  >
                    <option value="">{t("crm.selectStagePlaceholder")}</option>
                    {availableStages.map((s: any) => (
                      <option key={s.id} value={s.id}>
                        {s.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="flex justify-end gap-2 mt-6">
                <button
                  onClick={() => {
                    setShowBulkMoveDialog(false);
                    setBulkMovePipelineId("");
                    setBulkMoveStageId("");
                  }}
                  className="px-3 py-2 rounded-lg border border-border"
                >
                  {t("common.cancel")}
                </button>
                <button
                  onClick={() => {
                    if (bulkMoveStageId) {
                      handleBulkMove(bulkMoveStageId, bulkMovePipelineId || undefined);
                    }
                  }}
                  disabled={!bulkMoveStageId || bulkMove.isPending}
                  className="px-3 py-2 rounded-md bg-primary text-primary-foreground disabled:opacity-50"
                >
                  {bulkMove.isPending ? t("common.loading") : t("crm.move")}
                </button>
              </div>
            </div>
          </div>
          </>
        );
      })()}

      {/* Bulk Owner Change Dialog */}
      {showBulkOwnerDialog && (
        <>
          {/* safe-area: top inset */}
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 pt-[env(safe-area-inset-top)] px-[env(safe-area-inset-right)] pb-[env(safe-area-inset-bottom)] pl-[env(safe-area-inset-left)]">
          <div className="bg-background rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">{t("crm.changeOwner")}</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">{t("common.selectOwner")}</label>
                {usersQuery.isLoading ? (
                  <div className="text-sm text-foreground/60">{t("common.loading")}</div>
                ) : usersQuery.isError ? (
                  <div className="text-sm text-destructive">
                    {t("crm.loadUsersError")}
                  </div>
                ) : !usersQuery.data || usersQuery.data.length === 0 ? (
                  <div className="text-sm text-foreground/60">{t("common.noUsersAvailable")}</div>
                ) : (
                  <select
                    value={selectedOwnerEmail}
                    onChange={(e) => setSelectedOwnerEmail(e.target.value)}
                    className="w-full px-3 py-2 rounded-lg border border-border bg-background"
                  >
                    <option value="">({t("common.selectOwner")})</option>
                    {usersQuery.data.map((u) => (
                      <option key={u.id} value={u.email}>
                        {u.name || u.email} {u.role ? `(${u.role})` : ""}
                      </option>
                    ))}
                  </select>
                )}
              </div>
              <div className="text-sm text-foreground/70">
                {selectedDeals.size} deal tulajdonosa módosítódik.
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button
                onClick={() => {
                  setShowBulkOwnerDialog(false);
                  setSelectedOwnerEmail("");
                }}
                className="px-3 py-2 rounded-md border"
              >
                {t("common.cancel")}
              </button>
              <button
                onClick={() => {
                  if (selectedOwnerEmail) {
                    bulkOwnerChange.mutate({
                      dealIds: Array.from(selectedDeals),
                      ownerEmail: selectedOwnerEmail,
                    });
                  }
                }}
                disabled={!selectedOwnerEmail || bulkOwnerChange.isPending || usersQuery.isLoading}
                className="px-3 py-2 rounded-md bg-primary text-primary-foreground disabled:opacity-50"
              >
                {bulkOwnerChange.isPending ? t("common.modifying") : t("common.modify")}
              </button>
            </div>
            {bulkOwnerChange.isError && (
              <div className="mt-4 p-3 bg-red-100 text-red-700 rounded text-sm">
                {t("common.error")}: {(bulkOwnerChange.error as any)?.message || t("crm.ownerChangeFailed")}
              </div>
            )}
            {bulkOwnerChange.isSuccess && (
                <div className="mt-4 p-3 bg-green-50 dark:bg-green-950/30 text-green-700 dark:text-green-400 rounded-lg text-sm border border-green-200 dark:border-green-800">
                {t("crm.ownerChangeSuccess", { count: bulkOwnerChange.data?.success || 0 })}
              </div>
            )}
          </div>
        </div>
        </>
      )}

      {/* Export Dialog */}
      {showExportDialog && (
        <>
          {/* safe-area: top inset */}
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 pt-[env(safe-area-inset-top)] px-[env(safe-area-inset-right)] pb-[env(safe-area-inset-bottom)] pl-[env(safe-area-inset-left)]">
          <div className="bg-background rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold mb-4">{t("crm.exportTitle")}</h3>
            <div className="space-y-4">
              {/* Format Selection */}
              <div>
                <label className="block text-sm font-medium mb-2">{t("crm.exportFormatLabel")}</label>
                <div className="flex gap-3">
                  <button
                    onClick={() => setExportFormat("csv")}
                    className={`flex-1 px-4 py-3 rounded-md border transition-colors ${
                      exportFormat === "csv"
                        ? "bg-primary text-primary-foreground border-primary"
                        : "bg-background hover:bg-muted"
                    }`}
                  >
                    <Download className="h-5 w-5 mx-auto mb-1" />
                    <div className="text-sm font-medium">{t("crm.exportFormatCsv")}</div>
                  </button>
                  <button
                    onClick={() => setExportFormat("excel")}
                    className={`flex-1 px-4 py-3 rounded-md border transition-colors ${
                      exportFormat === "excel"
                        ? "bg-primary text-primary-foreground border-primary"
                        : "bg-background hover:bg-muted"
                    }`}
                  >
                    <FileSpreadsheet className="h-5 w-5 mx-auto mb-1" />
                    <div className="text-sm font-medium">{t("crm.exportFormatExcel")}</div>
                  </button>
                  <button
                    onClick={() => setExportFormat("pdf")}
                    className={`flex-1 px-4 py-3 rounded-md border transition-colors ${
                      exportFormat === "pdf"
                        ? "bg-primary text-primary-foreground border-primary"
                        : "bg-background hover:bg-muted"
                    }`}
                  >
                    <FileText className="h-5 w-5 mx-auto mb-1" />
                    <div className="text-sm font-medium">{t("crm.exportFormatPdf")}</div>
                  </button>
                </div>
              </div>

              {/* Column Selection */}
              <div>
                <label className="block text-sm font-medium mb-2">{t("crm.exportColumnsLabel")}</label>
                <div className="border rounded-md p-3 max-h-48 overflow-y-auto">
                  <div className="space-y-2">
                    {availableColumns.map((col) => (
                      <label key={col.id} className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={selectedColumns.has(col.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedColumns((prev) => new Set([...prev, col.id]));
                            } else {
                              setSelectedColumns((prev) => {
                                const next = new Set(prev);
                                next.delete(col.id);
                                return next;
                              });
                            }
                          }}
                          className="rounded"
                        />
                        <span className="text-sm">{t(col.labelKey)}</span>
                      </label>
                    ))}
                  </div>
                </div>
                <div className="mt-2 flex gap-2">
                  <button
                    onClick={() => setSelectedColumns(new Set(availableColumns.map(c => c.id)))}
                    className="text-xs text-primary hover:underline"
                  >
                    {t("crm.exportSelectAll")}
                  </button>
                  <button
                    onClick={() => setSelectedColumns(new Set())}
                    className="text-xs text-primary hover:underline"
                  >
                    {t("crm.exportDeselectAll")}
                  </button>
                </div>
              </div>

              <div className="text-sm text-foreground/70">
                {t("crm.exportDealsCount", { count: selectedDeals.size })}
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button
                onClick={() => setShowExportDialog(false)}
                className="px-3 py-2 rounded-md border"
              >
                {t("common.cancel")}
              </button>
              <button
                onClick={() => doExport(exportFormat)}
                disabled={selectedColumns.size === 0 || selectedDeals.size === 0}
                className="px-3 py-2 rounded-md bg-primary text-primary-foreground disabled:opacity-50"
              >
                {t("crm.export")}
              </button>
            </div>
          </div>
        </div>
        </>
      )}

      {/* Floating Action Button (Mobile) — opens action menu */}
      {isMobile && (
        <FabMenu
          actions={[
            {
              id: "new-deal",
              label: t("crm.newDeal"),
              icon: <Plus className="h-5 w-5" />,
              onClick: () => setOpen(true),
            },
            {
              id: "measure",
              label: t("crm.startMeasurement"),
              icon: <BarChart3 className="h-5 w-5" />,
              onClick: () => navigate("/crm/measure"),
            },
            {
              id: "handover",
              label: t("crm.startHandover"),
              icon: <ClipboardCheck className="h-5 w-5" />,
              onClick: () => {
                const pid = effectiveSubPipelineId;
                const q = pid ? `?pipelineId=${encodeURIComponent(pid)}` : "";
                navigate(`/crm/handover${q}`);
              },
            },
          ]}
        />
      )}

      <InstallationDateDialog
        open={!!installationPrompt}
        onOpenChange={(open) => {
          if (!open) setInstallationPrompt(null);
        }}
        dealTitle={installationPrompt?.dealTitle}
        confirming={installationConfirming}
        onConfirm={async (installationDate) => {
          if (!installationPrompt) return;
          setInstallationConfirming(true);
          try {
            move.mutate({
              id: installationPrompt.dealId,
              stageId: installationPrompt.stageId,
              installationDate,
            });
            setInstallationPrompt(null);
          } finally {
            setInstallationConfirming(false);
          }
        }}
      />
    </div>
  );
}
